import { EstimatorAvatar, Typography } from '@paintscout/ui'
import classnames from 'classnames'
// Solid, for the company meta row only — see headerIcons. The columns below it are secondary, so
// their detail lines keep the outlined Material icons (ENG-1366).
import { LocationIcon as MetaLocationIcon, PhoneIcon as MetaPhoneIcon, EventIcon } from './headerIcons'
import LocationOnIcon from '@material-ui/icons/LocationOnOutlined'
import PhoneIcon from '@material-ui/icons/PhoneOutlined'
import MailIcon from '@material-ui/icons/MailOutline'
import AddIcon from '@material-ui/icons/Add'
import React from 'react'
import { getActivityColor } from '@paintscout/util'
import { openMailto, openMap, openTel } from '../../util/deepLinks'
import { activatable } from './activatable'
import HeaderLineLink from './HeaderLineLink'
import { useQuoteDocumentHeaderStyles } from './quoteDocumentHeaderStyles'
import type { QuoteDocumentHeaderData } from './useQuoteDocumentHeaderData'

/** The three document-header columns a template shows as inactive placeholders. */
const TEMPLATE_COLUMN_LABELS = ['Prepared for', 'Job Address', 'Prepared by']

export interface QuoteDocumentHeaderDesktopProps {
  /** Render without the card's own border/rounding/gap — for embedding in a shared card. */
  flush?: boolean
  data: QuoteDocumentHeaderData
}

/**
 * The full document header: a company block (logo, name, address/phone/date meta row) with the doc
 * label + ID pinned right, then a Prepared for / Job Address / Prepared by column row separated by
 * painted vertical rules. A Secondary Contact column is inserted after Prepared for when the estimate
 * has one; the row wraps (3-up → 2-up → stacked) rather than squeezing four columns onto one line.
 *
 * The company block itself is not clickable — only the Issued/Expires chip is. Each lower column opens
 * its matching editor when the estimate is editable: Prepared for → the contact menu, Job Address →
 * JobAddressDialog, Prepared by → the estimator picker. Column values (phone/email/address) deep-link
 * (tel:/mailto:/maps), mirroring the EditContact/EditDeal/EditCompany detail cards.
 */
export default function QuoteDocumentHeaderDesktop({ flush, data }: QuoteDocumentHeaderDesktopProps) {
  const classes = useQuoteDocumentHeaderStyles()
  const {
    isEditable,
    onViewContact,
    onEditJobAddress,
    onSelectEstimator,
    companyName,
    logo,
    companyAddress,
    companyPhone,
    companyAdditional,
    isTemplate,
    isWorkOrder,
    docLabel,
    quoteId,
    issued,
    expires,
    workOrderDate,
    contact,
    contactName,
    contactCompany,
    contactAddressLines,
    contactPhones,
    contactEmails,
    contactClickable,
    activateContact,
    hasContact,
    secondaryContactName,
    secondaryContactCompany,
    secondaryContactAddressLines,
    secondaryContactPhones,
    secondaryContactEmails,
    secondaryContactClickable,
    hasSecondaryContact,
    activateSecondaryContact,
    jobIdentifier,
    jobAddressLines,
    jobAddressClickable,
    hasJobAddress,
    owner,
    ownerUser,
    userPictures,
    estimatorName,
    estimatorEmail,
    estimatorPhone,
    estimatorClickable,
    hasEstimator,
    setDateAnchorEl
  } = data

  // Label row: the accent tick + caption. The tick goes muted when the column is empty — keyed off
  // emptiness alone, so a read-only empty column is muted too.
  const renderColumnLabel = (label: string, isEmpty: boolean) => (
    <div className={classes.labelRow}>
      <span className={classnames(classes.tick, { [classes.tickEmpty]: isEmpty })} />
      <Typography variant="caption" className={classes.columnLabel}>
        {label}
      </Typography>
    </div>
  )

  // Dashed "add" placeholder for a column with no value yet. The enclosing column already owns the
  // click handler (and its keyboard equivalent), so this is presentation only.
  const renderEmptyState = (label: string) => (
    <div className={classes.emptyState}>
      <AddIcon className={classes.emptyStateIcon} aria-hidden />
      {label}
    </div>
  )

  // The same dashed box without the plus, for a column that is not an invitation to fill anything in:
  // a template's contact, address and estimator are filled on the estimates made from it.
  const renderInactiveState = (label: string) => <div className={classes.emptyState}>{label}</div>

  // The detail lines a contact column carries below its name: email, phone, and the mailing address —
  // all deep-links. Shared by "Prepared for" and "Secondary Contact" so the two read identically.
  const renderContactLines = (args: { emails: string[]; phones: string[]; addressLines: string[] }) => (
    <>
      {args.emails.map((email) => (
        <HeaderLineLink key={email} icon={MailIcon} onClick={() => openMailto(email)}>
          {email}
        </HeaderLineLink>
      ))}
      {args.phones.map((phone) => (
        <HeaderLineLink key={phone} icon={PhoneIcon} onClick={() => openTel(phone)}>
          {phone}
        </HeaderLineLink>
      ))}
      {args.addressLines.length > 0 && (
        <HeaderLineLink icon={LocationOnIcon} alignTop onClick={() => openMap(args.addressLines.join(', '))}>
          <span className={classes.addressLines}>
            {args.addressLines.map((line, index) => (
              // eslint-disable-next-line react/no-array-index-key
              <span key={index}>{line}</span>
            ))}
          </span>
        </HeaderLineLink>
      )}
    </>
  )

  return (
    <div className={[classes.root, flush && classes.flush].filter(Boolean).join(' ')}>
      <div className={classes.companySection}>
        <div className={classes.companyBlock}>
          {logo && <img className={classes.logo} src={logo} alt={companyName || 'Company logo'} />}
          <div className={classes.companyInfo}>
            <Typography component="div" className={classes.heading}>
              {companyName}
            </Typography>
            <div className={classes.metaRow}>
              {companyAddress?.length > 0 && (
                <span className={classes.metaItem}>
                  <MetaLocationIcon className={classes.metaIcon} />
                  {companyAddress.join(', ')}
                </span>
              )}
              {companyPhone && (
                <span className={classes.metaItem}>
                  <MetaPhoneIcon className={classes.metaIcon} />
                  {companyPhone}
                </span>
              )}
              {isWorkOrder && workOrderDate && (
                <span className={classes.metaItem}>
                  <EventIcon className={classes.metaIcon} />
                  {workOrderDate}
                </span>
              )}
              {/* A template has no issue or expiry date — those are stamped on the estimates made from
                  it — so the date chip is dropped rather than shown empty (ENG-1282). */}
              {!isWorkOrder && !isTemplate && (issued || expires) && (
                <span
                  className={classnames(classes.datePill, { [classes.datePillClickable]: isEditable })}
                  {...activatable(isEditable, setDateAnchorEl)}
                >
                  <EventIcon className={classes.metaIcon} />
                  {[issued, expires && `Expires: ${expires}`].filter(Boolean).join(' • ')}
                </span>
              )}
            </div>
            {companyAdditional && (
              <div className={classes.additionalRow}>
                <span className={classnames(classes.tick, classes.additionalTick)} />
                <Typography component="div" variant="caption" className={classes.additionalText}>
                  {companyAdditional}
                </Typography>
              </div>
            )}
          </div>
        </div>
        <div className={classes.docBlock}>
          <Typography component="div" className={classes.heading}>
            {docLabel}
          </Typography>
          {quoteId && (
            <Typography component="div" variant="body2" className={classes.docId}>
              ID {quoteId}
            </Typography>
          )}
        </div>
      </div>

      {!isWorkOrder && isTemplate && (
        <div className={classes.columns}>
          {TEMPLATE_COLUMN_LABELS.map((label) => (
            <div key={label} className={classes.column}>
              {renderColumnLabel(label, true)}
              {renderInactiveState('Filled on Estimate')}
            </div>
          ))}
        </div>
      )}

      {!isWorkOrder && !isTemplate && (
        <div className={classes.columns}>
          <div
            className={classnames(classes.column, {
              [classes.clickable]: contactClickable && hasContact,
              [classes.clickableEmpty]: contactClickable && !hasContact
            })}
            {...activatable(contactClickable, activateContact)}
            aria-haspopup={contact ? 'true' : undefined}
          >
            {renderColumnLabel('Prepared for', !hasContact)}
            {!hasContact && contactClickable ? (
              renderEmptyState('Add Contact')
            ) : (
              <div className={classes.nameGroup}>
                <Typography
                  component="div"
                  variant="body2"
                  className={classnames(classes.primaryText, classes.truncate)}
                >
                  {contact && onViewContact ? (
                    <span
                      className={classes.contactNameLink}
                      {...activatable(true, onViewContact, { stopPropagation: true })}
                    >
                      {contactName}
                    </span>
                  ) : (
                    contactName || '—'
                  )}
                </Typography>
                {contactCompany && (
                  <Typography component="div" variant="body2" className={classes.mutedText}>
                    {contactCompany}
                  </Typography>
                )}
              </div>
            )}
            {renderContactLines({
              emails: contactEmails,
              phones: contactPhones,
              addressLines: contactAddressLines
            })}
          </div>

          {/* The secondary contact gets its own column, immediately after the primary one, and only
              once one has been added — it is added from the primary contact's menu, so there is no
              dashed "add" placeholder here. Activating it opens the secondary contact menu
              (Edit / Swap or Create / Remove). */}
          {hasSecondaryContact && (
            <div
              className={classnames(classes.column, { [classes.clickable]: secondaryContactClickable })}
              {...activatable(secondaryContactClickable, activateSecondaryContact)}
              aria-haspopup={secondaryContactClickable ? 'true' : undefined}
            >
              {renderColumnLabel('Secondary Contact', false)}
              <div className={classes.nameGroup}>
                <Typography
                  component="div"
                  variant="body2"
                  className={classnames(classes.primaryText, classes.truncate)}
                >
                  {secondaryContactName || '—'}
                </Typography>
                {secondaryContactCompany && (
                  <Typography component="div" variant="body2" className={classes.mutedText}>
                    {secondaryContactCompany}
                  </Typography>
                )}
              </div>
              {renderContactLines({
                emails: secondaryContactEmails,
                phones: secondaryContactPhones,
                addressLines: secondaryContactAddressLines
              })}
            </div>
          )}

          <div
            className={classnames(classes.column, {
              [classes.clickable]: jobAddressClickable && hasJobAddress,
              [classes.clickableEmpty]: jobAddressClickable && !hasJobAddress
            })}
            {...activatable(jobAddressClickable, onEditJobAddress)}
          >
            {renderColumnLabel('Job Address', !hasJobAddress)}
            {jobIdentifier ? (
              // Has a job identifier: identifier is the bold primary value; the address sits below as a
              // muted, pin-prefixed deep-link line.
              <>
                <div className={classes.nameGroup}>
                  <Typography
                    component="div"
                    variant="body2"
                    className={classnames(classes.primaryText, classes.truncate)}
                  >
                    {jobIdentifier}
                  </Typography>
                </div>
                {jobAddressLines.length > 0 && (
                  <HeaderLineLink icon={LocationOnIcon} alignTop onClick={() => openMap(jobAddressLines.join(', '))}>
                    <span className={classes.addressLines}>
                      {jobAddressLines.map((line, index) => (
                        // eslint-disable-next-line react/no-array-index-key
                        <span key={index}>{line}</span>
                      ))}
                    </span>
                  </HeaderLineLink>
                )}
              </>
            ) : jobAddressLines.length > 0 ? (
              // No job identifier: promote the address to the column's primary value — first line
              // bold/bigger (like a name), remaining lines muted, no pin. Still deep-links to maps.
              <span
                className={classnames(classes.nameGroup, classes.lineLink)}
                {...activatable(true, () => openMap(jobAddressLines.join(', ')), { stopPropagation: true })}
              >
                {jobAddressLines.map((line, index) => (
                  <span
                    // eslint-disable-next-line react/no-array-index-key
                    key={index}
                    className={index === 0 ? classes.primaryText : classes.mutedText}
                  >
                    {line}
                  </span>
                ))}
              </span>
            ) : jobAddressClickable ? (
              renderEmptyState('Add Address')
            ) : (
              <div className={classes.nameGroup}>
                <Typography component="div" variant="body2" className={classes.primaryText}>
                  —
                </Typography>
              </div>
            )}
          </div>

          <div
            className={classnames(classes.column, {
              [classes.clickable]: estimatorClickable && hasEstimator,
              [classes.clickableEmpty]: estimatorClickable && !hasEstimator,
              // Only when the avatar is actually there to be avoided.
              [classes.columnWithAvatar]: !!owner
            })}
            {...activatable(estimatorClickable, onSelectEstimator)}
          >
            {owner && (
              <span className={classes.salesRepAvatar}>
                <EstimatorAvatar
                  user={{ id: owner.id, firstName: owner.firstName, lastName: owner.lastName }}
                  userPictures={userPictures}
                  width="32px"
                  height="32px"
                  renderEmpty="blank"
                  hideTooltip
                  avatarColor={getActivityColor(ownerUser?.user_metadata?.color)}
                />
              </span>
            )}
            {renderColumnLabel('Prepared by', !hasEstimator)}
            {!hasEstimator && estimatorClickable ? (
              renderEmptyState('Assign Estimator')
            ) : (
              <div className={classes.nameGroup}>
                <Typography
                  component="div"
                  variant="body2"
                  className={classnames(classes.primaryText, classes.truncate)}
                >
                  {estimatorName || '—'}
                </Typography>
                {owner?.title && (
                  <Typography component="div" variant="body2" className={classes.mutedText}>
                    {owner.title}
                  </Typography>
                )}
              </div>
            )}
            {estimatorEmail && (
              <HeaderLineLink icon={MailIcon} onClick={() => openMailto(estimatorEmail)}>
                {estimatorEmail}
              </HeaderLineLink>
            )}
            {estimatorPhone && (
              <HeaderLineLink icon={PhoneIcon} onClick={() => openTel(estimatorPhone)}>
                {estimatorPhone}
              </HeaderLineLink>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
